import { Component, computed, Inject, inject, model, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faClose, faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { finalize } from 'rxjs';

import { NotificationLevel, NotificationsService } from '@eiq/notifications';
import { UiAdminModule } from '@eiq/ui';

import { Language } from '@empathy/survey-core';

import { EditorTranslationsService } from '../../../services/editor-translations.service';

@Component({
  standalone: true,
  templateUrl: 'add-language-dialog.component.html',
  styleUrls: ['./add-language-dialog.component.scss'],
  imports: [FontAwesomeModule, FormsModule, MatDialogModule, UiAdminModule]
})
export class AddLanguageDialogComponent implements OnInit {
  search$ = model('');

  languages: Language[] = [];

  filteredLanguages$ = computed<Language[]>(() => {
    const q = this.search$().trim().toLowerCase();

    if (!q) {
      return this.languages;
    }

    return this.languages.filter(
      (lang) => lang.name.toLowerCase().includes(q) || lang.code.toLowerCase().includes(q) || lang.nativeName?.toLowerCase().includes(q)
    );
  });

  selectedLanguage$ = signal<Language | null>(null);

  loading$ = signal(false);

  faClose = faClose;
  faGlobe = faGlobe;

  private translationsService = inject(EditorTranslationsService);
  private notificationService = inject(NotificationsService);

  constructor(
    private dialogRef: MatDialogRef<AddLanguageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { languages: Language[] }
  ) {
    this.languages = data?.languages ?? [];
  }

  ngOnInit(): void {
    this.dialogRef.addPanelClass('eiq-add-language-dialog');
  }

  onAddLanguage(): void {
    this.loading$.set(true);

    this.translationsService
      .addLanguage(this.selectedLanguage$().code)
      .pipe(finalize(() => this.loading$.set(false)))
      .subscribe({
        next: () => this.dialogRef.close(),
        error: () =>
          this.notificationService.open({
            level: NotificationLevel.Error,
            title: 'Error',
            message: 'There was an error adding the language. Please, try again later.'
          })
      });
  }

  onCancel(): void {
    if (this.loading$()) {
      return;
    }

    this.dialogRef.close();
  }
}
